import * as React from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  InputBase,
  List,
  ListItemButton,
  Skeleton,
  Typography,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Data from '../Data/Data.json';
import Menus from '../Models/Menu';
import Utilisateur from '../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../Services/Auth/sessionAuth';
import { Crypter } from '../Functions/Security/Crypter';
import { getMenuAncestorKeys, requestMenuFocus } from '../Functions/MenuFocus';

interface SearchableMenu {
  codmen: string;
  label: string;
  lien: string;
  /** Chaîne des menus parents, du plus haut au plus proche */
  path: string[];
}

interface MenuSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const MAX_RESULTS = 40;
const SUGGESTION_COUNT = 8;

/** Supprime les accents et met en minuscule pour une comparaison tolérante */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Découpe un libellé en segments pour mettre en évidence la partie recherchée */
function highlight(label: string, search: string): React.ReactNode {
  if (!search) return label;
  const index = normalize(label).indexOf(normalize(search));
  if (index < 0) return label;
  return (
    <>
      {label.slice(0, index)}
      <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>
        {label.slice(index, index + search.length)}
      </Box>
      {label.slice(index + search.length)}
    </>
  );
}

export default function MenuSearchDialog({ open, onClose }: MenuSearchDialogProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuthUser();
  const authHeader = useAuthHeader();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [menus, setMenus] = React.useState<Menus[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(0);

  const listRef = React.useRef<HTMLUListElement | null>(null);

  const labelOf = React.useCallback(
    (menu: Menus) => (i18n.language === 'en' ? menu.libelmenan : menu.libelmenfr) || menu.libelmenfr || menu.codmen,
    [i18n.language],
  );

  // Chargement des menus habilités (une seule fois par session de dialogue)
  React.useEffect(() => {
    if (!open || loaded || loading) return;

    setLoading(true);
    setError(null);
    axios
      .request({
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.userMenus,
        headers: {
          Authorization: authHeader(),
          'Content-Type': 'application/json',
          CipherUserGroup: Crypter.encrypt(user.codgroup),
        },
      })
      .then((response) => {
        if (!response.data) return;
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200 && Array.isArray(res.Result)) {
          setMenus(res.Result);
          setLoaded(true);
        } else {
          setError(res?.Message || 'Aucune habilitation disponible.');
        }
      })
      .catch(() => setError("Impossible de charger les habilitations."))
      .finally(() => setLoading(false));
  }, [open, loaded, loading, authHeader, user.codgroup]);

  // Construit la liste plate des entrées navigables avec leur fil d'Ariane
  const searchable = React.useMemo<SearchableMenu[]>(() => {
    const byCode = new Map<string, Menus>();
    menus.forEach((menu) => byCode.set(menu.codmen, menu));

    // Le fil d'Ariane suit la même logique de préfixes que le menu latéral
    const buildPath = (menu: Menus): string[] =>
      getMenuAncestorKeys(menu.codmen)
        .map((key) => byCode.get(key))
        .filter((parent): parent is Menus => Boolean(parent))
        .map(labelOf);

    return menus
      .filter((menu) => Boolean(menu.lien))
      .map((menu) => ({
        codmen: menu.codmen,
        label: labelOf(menu),
        lien: menu.lien as string,
        path: buildPath(menu),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [menus, labelOf]);

  const results = React.useMemo(() => {
    if (!search.trim()) return searchable.slice(0, SUGGESTION_COUNT);
    const needle = normalize(search.trim());
    return searchable
      .filter(
        (item) =>
          normalize(item.label).includes(needle) ||
          normalize(item.path.join(' ')).includes(needle) ||
          normalize(item.lien).includes(needle),
      )
      // Les correspondances en début de libellé remontent en premier
      .sort((a, b) => {
        const aStarts = normalize(a.label).startsWith(needle) ? 0 : 1;
        const bStarts = normalize(b.label).startsWith(needle) ? 0 : 1;
        return aStarts - bStarts;
      })
      .slice(0, MAX_RESULTS);
  }, [search, searchable]);

  React.useEffect(() => setActiveIndex(0), [search]);

  // Réinitialise la recherche à chaque ouverture
  React.useEffect(() => {
    if (open) {
      setSearch('');
      setActiveIndex(0);
    }
  }, [open]);

  const go = React.useCallback(
    (item: SearchableMenu) => {
      onClose();
      // Met en évidence l'entrée correspondante dans le menu latéral
      requestMenuFocus(item.codmen);
      if (item.lien.includes('statistics')) {
        window.open('https://srv-anybank/Reports/', '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(item.lien);
    },
    [navigate, onClose],
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (results.length ? (prev + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (results.length ? (prev - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = results[activeIndex];
      if (item) go(item);
    }
  };

  // Garde l'élément actif visible pendant la navigation clavier
  React.useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const isEmptySearch = !search.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          alignSelf: 'flex-start',
          mt: '10vh',
        },
      }}
      sx={{ '& .MuiBackdrop-root': { backdropFilter: 'blur(3px)' } }}
    >
      {/* Barre de saisie */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
        }}
      >
        <SearchIcon color="primary" />
        <InputBase
          autoFocus
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('Rechercher un menu, un écran…') as string}
          sx={{ fontSize: 16 }}
        />
        {loading && <CircularProgress size={18} />}
        <Chip
          size="small"
          label="Esc"
          onClick={onClose}
          icon={<CloseIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: 11, height: 22 }}
        />
      </Box>

      <Divider />

      {/* Résultats */}
      <Box sx={{ maxHeight: '52vh', overflowY: 'auto' }}>
        {loading && !loaded ? (
          <Box sx={{ p: 2 }}>
            {[0, 1, 2, 3, 4].map((key) => (
              <Skeleton key={key} variant="rectangular" height={34} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center', color: 'text.secondary' }}>
            <SearchOffIcon sx={{ fontSize: 42, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Aucun écran habilité ne correspond à «&nbsp;{search}&nbsp;»
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', fontWeight: 600 }}
            >
              {isEmptySearch ? 'Accès rapides' : `${results.length} résultat${results.length > 1 ? 's' : ''}`}
            </Typography>
            <List ref={listRef} dense disablePadding sx={{ pb: 1 }}>
              {results.map((item, index) => (
                <ListItemButton
                  key={item.codmen}
                  data-index={index}
                  selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => go(item)}
                  sx={{
                    mx: 1,
                    borderRadius: 1.5,
                    '&.Mui-selected': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {highlight(item.label, search.trim())}
                    </Typography>
                    {item.path.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 0.25 }}>
                        {item.path.map((step, stepIndex) => (
                          <React.Fragment key={`${item.codmen}-${stepIndex}`}>
                            {stepIndex > 0 && <ChevronRightIcon sx={{ fontSize: 12, mx: 0.15 }} />}
                            <Typography variant="caption" noWrap>
                              {step}
                            </Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                    )}
                  </Box>
                  {index === activeIndex && <KeyboardReturnIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </Box>

      <Divider />

      {/* Aide clavier */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          color: 'text.secondary',
        }}
      >
        <Typography variant="caption">↑ ↓ naviguer</Typography>
        <Typography variant="caption">↵ ouvrir</Typography>
        <Typography variant="caption">Esc fermer</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption">{searchable.length} écrans habilités</Typography>
      </Box>
    </Dialog>
  );
}
